/**
 * Helper utilities for Cost Optimization feature
 */

type ImpactLevel = 'High' | 'Medium' | 'Low';
type EffortLevel = 'High' | 'Medium' | 'Low';
type RecommendationType = 'Idle' | 'Rightsizing' | 'Reserved Instances' | 'Storage Optimization' | 'Savings Plan';
type RecommendationStatus = 'Open' | 'Implemented' | 'Dismissed' | 'Auto_Resolved';

export interface Recommendation {
  service: string;
  type: RecommendationType;
  status: RecommendationStatus;
  monthly_savings: number;
}

interface RecommendationSummary {
  openCount: number;
  monthlySavings: number;
  annualSavings: number;
  implementedCount: number;
}

interface CategoryCounts {
  all: number;
  rightsizing: number;
  idleResources: number;
  reservedInstances: number;
  storageOptimization: number;
  savingsPlan: number;
}

const serviceIconMap: Record<string, string> = {
  EC2: 'FiServer',
  RDS: 'FiDatabase',
  Lambda: 'FiZap',
  S3: 'FiHardDrive',
  DynamoDB: 'FiDatabase',
  ECS: 'FiBox',
  EKS: 'FiBox',
  ALB: 'FiLayers',
  SageMaker: 'FiCpu',
  EBS: 'FiHardDrive',
  Bedrock: 'FiCpu',
  Glue: 'FiDatabase',
  EMR: 'FiServer',
};

/**
 * Calculate annual savings from monthly savings
 */
export const calculateAnnualSavings = (monthlySavings: number): number => {
  return monthlySavings * 12;
};

/**
 * Get impact level based on monthly savings amount
 */
export const getImpactLevel = (monthlySavings: number): ImpactLevel => {
  if (monthlySavings > 200) return 'High';
  if (monthlySavings > 50) return 'Medium';
  return 'Low';
};

/**
 * Get effort level based on service type
 */
export const getEffortLevel = (service: string): EffortLevel => {
  const lowEffortServices = ['Lambda', 'S3', 'DynamoDB', 'EBS', 'Elastic IP', 'CloudWatch Logs'];
  const mediumEffortServices = ['EC2', 'RDS', 'ECS', 'Glue', 'EMR', 'Bedrock'];

  if (lowEffortServices.includes(service)) return 'Low';
  if (mediumEffortServices.includes(service)) return 'Medium';
  return 'High';
};

/**
 * Format recommendation title based on service and type
 */
export const formatRecommendationTitle = (recommendation: Pick<Recommendation, 'service' | 'type'>): string => {
  const { service, type } = recommendation;
  const serviceLower = service.toLowerCase();

  if (type === 'Idle') {
    // S3 uses "Delete"
    if (service === 'S3') {
      return `Delete idle ${serviceLower} bucket`;
    }
    // EC2, RDS, Redshift use "Terminate"
    if (['EC2', 'RDS', 'Redshift'].includes(service)) {
      return `Terminate idle ${serviceLower} instance`;
    }
    // Other services use generic "Delete"
    return `Delete idle ${serviceLower} resource`;
  } else if (type === 'Rightsizing') {
    return `Downsize over-provisioned ${serviceLower} instance`;
  } else if (type === 'Reserved Instances') {
    return `Purchase Reserved Instances for ${serviceLower}`;
  } else if (type === 'Storage Optimization') {
    return `Optimize ${serviceLower} storage configuration`;
  } else if (type === 'Savings Plan') {
    return `Purchase Savings Plan for ${serviceLower}`;
  }

  return `Optimize ${serviceLower} resource`;
};

/**
 * Format currency value
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format date
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Get status badge color
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Open':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
    case 'Implemented':
      return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
    case 'Dismissed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    case 'Auto_Resolved':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

/**
 * Get confidence badge color
 */
export const getConfidenceColor = (confidence: string): string => {
  switch (confidence) {
    case 'High':
      return 'text-green-600 dark:text-green-400';
    case 'Medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'Low':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Get impact badge color
 */
export const getImpactColor = (impact: string): string => {
  switch (impact) {
    case 'High':
      return 'text-red-600 dark:text-red-400';
    case 'Medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'Low':
      return 'text-green-600 dark:text-green-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Get effort badge color
 */
export const getEffortColor = (effort: string): string => {
  switch (effort) {
    case 'Low':
      return 'text-green-600 dark:text-green-400';
    case 'Medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'High':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Get service icon component name (for react-icons)
 */
export const getServiceIcon = (service: string): string => {
  return serviceIconMap[service] || 'FiServer';
};

/**
 * Calculate summary statistics from recommendations
 */
export const calculateSummary = (recommendations: Recommendation[]): RecommendationSummary => {
  const openRecommendations = recommendations.filter(r => r.status === 'Open');

  return {
    openCount: openRecommendations.length,
    monthlySavings: openRecommendations.reduce((sum, r) => sum + r.monthly_savings, 0),
    annualSavings: openRecommendations.reduce((sum, r) => sum + (r.monthly_savings * 12), 0),
    implementedCount: recommendations.filter(r => r.status === 'Implemented').length,
  };
};

/**
 * Get category counts
 */
export const getCategoryCounts = (recommendations: Recommendation[]): CategoryCounts => {
  return {
    all: recommendations.length,
    rightsizing: recommendations.filter(r => r.type === 'Rightsizing').length,
    idleResources: recommendations.filter(r => r.type === 'Idle').length,
    reservedInstances: recommendations.filter(r => r.type === 'Reserved Instances').length,
    storageOptimization: recommendations.filter(r => r.type === 'Storage Optimization').length,
    savingsPlan: recommendations.filter(r => r.type === 'Savings Plan').length,
  };
};

